import { Address } from '../models/response/address/address';
import { User } from '../models/response/user';
import { Constant } from './constant';
import * as moment from 'moment';
import {
  ProspectListCustomCellComponent,
} from '../../modules/pages/prospect/common/prospect-list-custom-cell-component';
import { Prospect } from '../models/response/prospect/prospect';
import { Category } from '../models/response/category';

export class StringUtils {

  public static getHouseNumber(address: Partial<Address>): string {
    if (!address) { return ''; }
    let number = String(address.numberFrom || '');
    const letter = (address.numberFromLetter || '').trim();
    const normalizedTail = (value: string) => value.replace(/[-/.\s]/g, '').toUpperCase();
    if (letter && !normalizedTail(number).endsWith(normalizedTail(letter))) { number += letter; }
    if (address.numberTo) {
      number += ' - ' + address.numberTo + (address.numberToLetter || '');
    }
    const info = (address.additionalInfo || '').trim();
    const normalizedInfo = normalizedTail(info);
    const suffix = (address.numberTo ? address.numberToLetter : address.numberFromLetter) || '';
    const alreadyIncluded = normalizedInfo && (normalizedInfo === normalizedTail(suffix)
      || (address.numberTo && normalizedInfo === normalizedTail(String(address.numberTo) + (address.numberToLetter || '')))
      || (/^[-/.]?[a-z]+$/i.test(info) && normalizedTail(number).endsWith(normalizedInfo)));
    if (info && !alreadyIncluded) { number += /^[-/.]/.test(info) ? info : '-' + info; }
    return number;
  }

  public static getFullAddress(address: Address): string {
    let result = '';
    if (address) {
      const formatted = [];
      if (address.numberFrom || address.numberTo) {
        let number = '';
        number += address.numberFrom ? address.numberFrom : '';
        number += address.numberFromLetter ? address.numberFromLetter : '';
        number += address.numberFrom && address.numberTo ? ' - ' : '';
        number += address.numberTo ? address.numberTo : '';
        number += address.numberToLetter ? address.numberToLetter : '';
        formatted.push(number);
      }
      if (address.street) {
        formatted.push(address.street.name);
        if (address.street.district) {
          formatted.push(address.street.district.name);
          if (address.street.district.code) {
            // const parts = address.street.district.code.split('.');
            // formatted.push(parts[0].toString());
            // abc
            formatted.push(address.street.district.code.toString());
          }
          // if (address.street.district.city) {
          //   formatted.push(address.street.district.city.name);
          // }
        }
      }
      result += formatted.map(s => s.trim()).join(', ');
      if (address.unit) {
        result = address.unit + '/' + result;
      }
    }
    return result;
  }

  public static getShortAddress(address: Partial<Address>): string {
    let result = '';
    if (address) {
      const formatted = [];
      if (address.numberFrom) {
        const number = this.getHouseNumber(address);
        formatted.push(number);
      }
      if (address.street) {
        formatted.push(address.street.name);
        if (address.street.district) {
          formatted.push(address.street.district.name);
        }
        if (address.street.district?.code) {
          // const parts = address.street.district.code.toString().split('.');
          // formatted.push(parts[0].toString());
          formatted.push(address.street.district.code.toString());
        }
      } else {
        if (address.streetName) { formatted.push(address.streetName); }
        if (address.districtName) { formatted.push(address.districtName); }
        if (address.postCode) { formatted.push(String(address.postCode)); }
      }
      result += formatted.map(s => s.trim()).join(', ');
      if (address.unit) {
        result = address.unit + '/' + result;
      }
    }
    return result;
  }

  public static formatAddress(address: Address): string[] {
    const result = [];
    result.push(this.getUnitAddress(address));
    result.push(this.getFromNumber(address));
    result.push(this.getRemainingAddress(address));
    return result;
  }

  public static getFromNumber(address: Address): string {
    let result = '';
    if (address) {
      const formatted = [];
      if (address.numberFrom || address.numberTo) {
        let number = '';
        number += address.numberFrom ? address.numberFrom : '';
        formatted.push(number);
      }
      result += formatted.map(s => s.trim());
    }
    return result;
  }

  public static getRemainingAddress(address: Address): string {
    let result = '';
    if (address) {
      const formatted = [];
      if (address.numberFrom || address.numberTo) {
        const houseNumber = this.getHouseNumber(address);
        const number = houseNumber.substring(String(address.numberFrom || '').length);
        formatted.push(number);
      }
      if (address.street) {
        formatted.push(address.street.name);
        if (address.street.district) {
          formatted.push(address.street.district.name);
        }
      }
      result += formatted.map(s => s.trim());
    }
    return result;
  }

  public static getUnitAddress(address: Address): string {
    let result = '';
    if (address) {
      if (address.unit) {
        result += address.unit + ' / ' + result;
      } else {
        result = '';
      }
    }
    return result;
  }

  public static shortenUserName(user: User) {
    if (!user) {
      return '';
    }
    if (user.firstName || user.lastName) {
      return user?.firstName[0]?.toUpperCase() + user?.lastName[0]?.toUpperCase();
    }
    if (user.name) {
      return this.getShortName(user.name);
    }
    return this.getShortName(user.username);
  }

  static getShortName(name: string) {
    if (!name || !name.trim()) {
      return '';
    }
    const items: string[] = name.replace(/[\s]+/g, ' ').trim().split(' ');
    let result = '';
    for (const item of items) {
      result += item[0]?.toUpperCase();
    }
    return result;
  }

  static formatNumber(value: number): string {
    let unitNumber = 0;
    let unit = '';
    if (value >= 1000000000) {
      unitNumber = 1000000000;
      unit = 'B';
    } else if (value >= 1000000) {
      unitNumber = 1000000;
      unit = 'M';
    } else if (value >= 1000) {
      unitNumber = 1000;
      unit = 'K';
    } else {
      unitNumber = 1;
      unit = 'pw';
    }
    return StringUtils.formatFormatNumberResult(value / unitNumber) + unit;
  }

  static formatFormatNumberResult(value: number) {
    if ((value * 10) % 10 === 0) {
      return Math.round(value);
    }
    return value.toFixed(1);
  }

  static getFormattedValue(valueType: string, value: string): string {
    if (value) {
      switch (valueType) {
        case ProspectListCustomCellComponent.VALUE_TYPES.CURRENCY:
          value = new Intl.NumberFormat('en-IE').format(Number(value)).replace(',', '     ');
          // value = value.replace('.', ' ');
          value = `${value}${Constant.DEFAULT_CURRENCY_ICON}`;
          break;
        case ProspectListCustomCellComponent.VALUE_TYPES.DATE:
          value = moment(Number(value)).format('DD/MM/YYYY');
          break;
        default:
          break;
      }
    }
    return value;
  }

  static getNestedByPath(object: any, path: string): string {
    if (path && path.length > 0 && object) {
      const properties: string[] = path.split('.');
      if (properties && properties.length > 0) {
        const subObject = object[properties[0]];
        if (!subObject) {
          return '';
        }
        if (properties.length > 2) {
          properties.shift();
          return StringUtils.getNestedByPath(subObject, properties.join('.'));
        } else {
          return subObject[properties[1]];
        }
      } else {
        return object[path];
      }
    }
    return '';
  }

  static isNotEmpty(input: string): boolean {
    if (!input) {
      return false;
    }
    return input !== '';
  }

  static isEmpty(input: string): boolean {
    if (!input) {
      return true;
    }
    return input === '';
  }


  public static getClassification(prospect: Prospect): string {
    let result = '';
    if (prospect?.property?.sell) {
      result += 'S';
    }
    if (prospect?.property?.otm) {
      result += 'O';
    }
    if (prospect?.property?.rent) {
      result += 'R';
    }
    if (prospect?.property?.vac) {
      result += 'V';
    }
    result += '.';
    result += prospect?.rate;
    return result;
  }

  public static getFullName(prospect: Prospect): string {
    if (StringUtils.isEmpty(prospect?.customer?.name?.trim()) && StringUtils.isEmpty(prospect?.customer?.surname?.trim())) {
      return ' ';
    }
    let result = '';
    if (StringUtils.isNotEmpty(prospect?.customer?.name)) {
      result += prospect?.customer?.name;
    }
    if (StringUtils.isNotEmpty(prospect?.customer?.surname)) {
      result += ' ';
      result += prospect?.customer?.surname;
    }
    return result.trim();
  }

  public static getSpec(prospect: Prospect): string {
    let result = '';
    if (prospect?.property?.type && prospect?.property?.type.includes('_')) {
      const parts = prospect?.property?.type.split('_');
      for (let i = 0; i < parts.length; i++) {
        if (i === parts.length - 1) {
          if (parts[i].length === 2) {
            result += parts[i];
          } else {
            result += parts[i].substr(0, 1);
          }
        } else {
          result += parts[i].substr(0, 1);
        }
      }
    } else {
      result += prospect?.property?.type?.substr(0, 1);
    }
    result += '.';
    result += prospect?.property?.bed;
    result += '.';
    result += prospect?.property?.bath;
    result += '.';
    result += prospect?.property?.car;
    result += ' - ';
    result += prospect?.property?.land ? prospect?.property?.land : 0;
    return result;
  }

  static getLand(input: number): string[] {
    const result = [];
    if (!input) {
      result.push(' m²');
      result.push('0');
    }
    if (input >= 10000) {
      result.push((input.toFixed(2).toString()));
      result.push(' m²');
    } else {
      result.push(input);
      result.push(' m²');
    }
    return result;
  }

  static getAllTags(tags: Category[]): string {
    let result = '';
    if (tags && tags.length > 0) {
      for (let i = 0; i < tags.length; i++) {
        result += tags[i].name;
        if (i < tags.length - 1) {
          result += '-';
        }
      }
    }
    return result;
  }

  static getOrgNameForVCard(data: Prospect): string {
    const parts: string[] = [];

    if (data?.address?.postCode) {
      parts.push(String(data.address.postCode));
    }
    if (data?.address?.districtName) {
      parts.push(data.address.districtName);
    }
    if (data?.categories?.length) {
      parts.push(this.getAllTags(data.categories));
    }
    return parts.join('.');
  }
}
